import type { ItemStatus } from "@/lib/types";
import { MY_ITEMS_ACTIONABLE_SOLD_ATTENTION_STATUSES } from "@/lib/seller/myItemsTabStatusSets";

export type SellerMyItemsTabId =
  | "active"
  | "changes_needed"
  | "sold"
  | "history";

export type SellerMyItemsTabCounts = Record<SellerMyItemsTabId, number>;

export const EMPTY_SELLER_MY_ITEMS_TAB_COUNTS: SellerMyItemsTabCounts = {
  active: 0,
  changes_needed: 0,
  sold: 0,
  history: 0,
};

const ACTIONABLE_SOLD_SET = new Set<ItemStatus>(
  MY_ITEMS_ACTIONABLE_SOLD_ATTENTION_STATUSES,
);

/**
 * Classify a listing status into at most one attention bucket (availability handled separately).
 */
export function classifyItemAttentionBucket(
  status: string,
): SellerMyItemsTabId | null {
  if (status === "needs_user_action") {
    return "changes_needed";
  }
  if (status === "rejected") {
    return "history";
  }
  if (ACTIONABLE_SOLD_SET.has(status as ItemStatus)) {
    return "sold";
  }
  return null;
}

/**
 * Dedupe item rows into per-tab counts using bucket priority (for tests / future single-query paths).
 */
export function aggregateTabCountsFromItemStatuses(
  statuses: readonly string[],
  availabilityPendingCount: number,
): SellerMyItemsTabCounts {
  const counts: SellerMyItemsTabCounts = {
    ...EMPTY_SELLER_MY_ITEMS_TAB_COUNTS,
  };

  for (const status of statuses) {
    const bucket = classifyItemAttentionBucket(status);
    if (!bucket) continue;
    counts[bucket] += 1;
  }

  counts.active = availabilityPendingCount;
  return counts;
}

export function buildSellerMyItemsTabCounts(input: {
  changesNeededCount: number;
  rejectedCount: number;
  actionableSoldCount: number;
  availabilityPendingCount: number;
}): SellerMyItemsTabCounts {
  return {
    changes_needed: Math.max(0, input.changesNeededCount),
    history: Math.max(0, input.rejectedCount),
    sold: Math.max(0, input.actionableSoldCount),
    active: Math.max(0, input.availabilityPendingCount),
  };
}

export function needsAttentionFromTabCounts(
  counts: SellerMyItemsTabCounts,
): boolean {
  return (
    counts.active > 0 ||
    counts.changes_needed > 0 ||
    counts.sold > 0 ||
    counts.history > 0
  );
}

export function sumTabCounts(counts: SellerMyItemsTabCounts): number {
  return counts.active + counts.changes_needed + counts.sold + counts.history;
}

/** Tab label suffix, e.g. `Changes needed (2)` or `Active (99+)`. */
export function formatMyItemsTabLabel(
  baseLabel: string,
  count: number,
): string {
  if (count <= 0) return baseLabel;
  const display = count > 99 ? "99+" : String(count);
  return `${baseLabel} (${display})`;
}

const TAB_ATTENTION_NAMES: Record<SellerMyItemsTabId, string> = {
  active: "Active",
  changes_needed: "Changes needed",
  sold: "Sold",
  history: "History",
};

export function myItemsTabAriaLabel(
  tabId: SellerMyItemsTabId,
  count: number,
): string {
  const name = TAB_ATTENTION_NAMES[tabId];
  if (count <= 0) return name;
  const display = count > 99 ? "99 plus" : String(count);
  const listingWord = count === 1 ? "listing needs" : "listings need";
  return `${name}, ${display} ${listingWord} attention`;
}
